import { useCallback, useMemo, useState } from "react";
import { FetchFinancialDocumentsResponse } from "@common/dto/financialDocument/fetch-financial-documents-response.dto";
import { FiltersTypes } from "@common/types/filters/general";
import { FinancialDocumentListItem } from "@common/types/financialDocument";
import { FinancialDocumentSortValue } from "@common/types/views/financialDocumentViews";
import {
    FINANCIAL_DOCUMENTS_PAGE_SIZE,
    getFinancialDocumentsQueryKey,
    useFinancialDocumentsQuery,
} from "@components/documents/useFinancialDocumentsQuery";
import { getSortExpression } from "@components/documents/useFinancialDocumentSort";
import type { PaginationType } from "@hooks/paginationMode";
import { useLoadMorePages } from "@hooks/useLoadMorePages";
import { useLoadMoreTableState } from "@hooks/useLoadMoreTableState";
import { queryFinancialDocuments } from "@lib/financialDocument";
import { useScope } from "@state/scopeContext.lib";
import { buildFinancialDocumentsTaskQuery } from "./financialDocumentsTaskQuery";

interface UseFinancialDocumentsTaskRowsParams {
    viewFilters: FiltersTypes;
    originalFilters?: FiltersTypes;
    stickyDocumentIds?: number[];
    /** Numbered pages by default; the task body loads more on scroll. */
    paginationType?: PaginationType;
}

const selectDocuments = (response: FetchFinancialDocumentsResponse) =>
    response.data;
const getDocumentId = (document: FinancialDocumentListItem) => document.id;

export const useFinancialDocumentsTaskRows = ({
    viewFilters,
    originalFilters,
    stickyDocumentIds,
    paginationType = "pages",
}: UseFinancialDocumentsTaskRowsParams) => {
    const isLoadMore = paginationType === "loadMore";
    const { workspaceId } = useScope();
    const [pagedPage, setPagedPage] = useState(1);
    const page = isLoadMore ? 1 : pagedPage;
    const [sort, setSort] = useState(
        FinancialDocumentSortValue.UPLOAD_DATE_DESC,
    );

    const queryFilters = useMemo(
        () =>
            buildFinancialDocumentsTaskQuery({
                viewFilters,
                originalFilters,
                stickyDocumentIds,
            }),
        [viewFilters, originalFilters, stickyDocumentIds],
    );
    const isQueryEnabled = !!workspaceId && queryFilters !== null;

    // In load-more mode this is the first batch, which carries the total.
    const documents = useFinancialDocumentsQuery({
        queryParams: {
            page: page - 1,
            workspaceId: workspaceId ?? "",
            limit: FINANCIAL_DOCUMENTS_PAGE_SIZE,
            sort: getSortExpression(sort),
            filters: queryFilters ?? undefined,
        },
        additionalParams: { enabled: isQueryEnabled },
    });

    const loadedDocuments = useLoadMorePages({
        buildPageQuery: (pageNumber) => {
            const queryParams = {
                page: pageNumber - 1,
                workspaceId: workspaceId ?? "",
                limit: FINANCIAL_DOCUMENTS_PAGE_SIZE,
                sort: getSortExpression(sort),
                filters: queryFilters ?? undefined,
            };
            return {
                queryKey: getFinancialDocumentsQueryKey(queryParams),
                queryFn: () => queryFinancialDocuments(queryParams),
            };
        },
        select: selectDocuments,
        getRowId: getDocumentId,
        pageSize: FINANCIAL_DOCUMENTS_PAGE_SIZE,
        total: documents.data?.total,
        enabled: isLoadMore && isQueryEnabled,
    });
    const loadMoreState = useLoadMoreTableState(loadedDocuments);

    const handleSortChange = useCallback(
        (nextSort: FinancialDocumentSortValue) => {
            setSort(nextSort);
            setPagedPage(1);
        },
        [],
    );
    const resetPage = useCallback(() => setPagedPage(1), []);

    const pagedRows = documents.data?.data ?? null;
    const loadedRows = documents.data ? (loadedDocuments.rows ?? []) : null;
    const shownRows = isLoadMore ? loadedRows : pagedRows;

    return {
        queryFilters,
        rows: queryFilters === null ? [] : shownRows,
        total: queryFilters === null ? 0 : (documents.data?.total ?? 0),
        totalPages: isLoadMore ? 1 : (documents.data?.pageCount ?? 1),
        page,
        setPage: setPagedPage,
        resetPage,
        sort,
        handleSortChange,
        loadMore: isLoadMore ? loadMoreState : undefined,
    };
};
